import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
  CheckRounded,
  ContentCopyRounded,
  FileDownloadRounded
} from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import { useShowSnackbar } from '../../../utils/snackbar';
import {
  RECOVERY_CODES_FILENAME,
  recoveryCodesFileText
} from '../../../utils/recoveryCodes';
import { PT, ghostBtnSx, primaryBtnSx } from '../../../components/portalUi';

type RecoveryCodesPanelProps = {
  codes: string[];
  onDone: () => void;
};

/* The ten recovery codes, shown ONCE: the server keeps only their hashes, so this is
 * the member's one chance to save them. Done stays disabled until they tick that they
 * have, and the dialogs that show this panel cannot be closed any other way. Used by
 * the 2FA setup modal (the first set comes back with the verify response) and by
 * Settings → Security (Generate new codes). */
export const RecoveryCodesPanel = ({ codes, onDone }: RecoveryCodesPanelProps) => {
  const { user } = useUser();
  const { showSnackbar } = useShowSnackbar();
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showSnackbar(
        'Copying is blocked here. Use Download, or select the codes and copy them.',
        'warning'
      );
    }
  };

  const download = () => {
    const text = recoveryCodesFileText(codes, user?.email ?? '', new Date());
    const url = URL.createObjectURL(
      new Blob([text], { type: 'text/plain;charset=utf-8' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = RECOVERY_CODES_FILENAME;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // A beat later: some browsers only start reading the file after click() returns.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: PT.textMuted, lineHeight: 1.55 }}>
        If you lose your phone or can&apos;t use your authenticator app, each of these
        codes signs you in once, in place of the 6-digit code. Keep them somewhere safe
        and private, like a password manager or a printed copy.{' '}
        <Box component="strong" sx={{ color: PT.text }}>
          They won&apos;t be shown again.
        </Box>
      </Typography>

      {/* aria-label + data-track hand the analytics click capture (utils/track.ts) a
          safe label: a triple-click to select a code counts there as a rage-click, and
          with nothing labelled above it, it would record the code's own text. */}
      <Box
        component="ul"
        aria-label="Recovery codes"
        data-track="security.recovery_codes"
        sx={{
          listStyle: 'none',
          m: 0,
          my: 2,
          p: { xs: 1.5, sm: 2 },
          display: 'grid',
          // Two columns where a fifteen-character code fits twice at a size worth
          // reading; one on phones, where it does not.
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 2,
          rowGap: 1.25,
          borderRadius: '12px',
          border: `1px solid ${PT.border}`,
          background: 'rgba(255,255,255,0.03)'
        }}
      >
        {codes.map((code, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              textAlign: 'center',
              whiteSpace: 'nowrap',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              fontWeight: 700,
              fontSize: { xs: 16, sm: 15 },
              letterSpacing: '0.04em',
              color: PT.tealText
            }}
          >
            {code}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={copyAll}
          startIcon={copied ? <CheckRounded /> : <ContentCopyRounded />}
          sx={{ ...ghostBtnSx, flex: 1, py: 1, color: copied ? PT.green : PT.text }}
        >
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button
          onClick={download}
          startIcon={<FileDownloadRounded />}
          sx={{ ...ghostBtnSx, flex: 1, py: 1 }}
        >
          Download
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            sx={{ color: PT.textMuted, '&.Mui-checked': { color: PT.teal } }}
          />
        }
        label="I have saved these codes somewhere safe"
        sx={{
          mt: 1.5,
          mr: 0,
          '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 600, color: PT.text }
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button
          onClick={onDone}
          disabled={!saved}
          sx={{ ...primaryBtnSx, px: 4, py: 1, width: { xs: '100%', sm: 'auto' } }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};
